import { ArrowUp, Loader2, Mic, Square } from "lucide-react"
import type { ReactNode, RefObject } from "react"

import { MicrophoneMenu } from "@/components/microphone-menu"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import type { useDictation } from "@/lib/use-dictation"
import { cn } from "@/lib/utils"

type Props = {
  value: string
  onChange: (value: string) => void
  onSubmit: (text?: string) => void
  placeholder: string
  /** Locks input and sending, e.g. until a feedback exists. */
  disabled?: boolean
  /** A reply is streaming: sending turns into stopping. */
  busy?: boolean
  onStop?: () => void
  /** From `useDictation`; the bar owns the microphone button and the indicator. */
  dictation?: ReturnType<typeof useDictation>
  /** Left of the microphone — the call button. */
  actions?: ReactNode
  /** Above the bar (mode switch) and below it (demo hint). */
  above?: ReactNode
  below?: ReactNode
  /**
   * "md" for a chat that owns the screen, "sm" where the bar sits under a
   * report and must not shout.
   */
  size?: "sm" | "md"
  variant?: "default" | "hero"
  /**
   * "stacked" puts the actions on their own row below the field and labels the
   * send button. Born from user feedback in the roleplay: the arrow icon next
   * to the phone icon was not recognised as "send".
   */
  layout?: "inline" | "stacked"
  autoFocus?: boolean
  rows?: number
  /** Lets the surrounding screen put the cursor here from elsewhere. */
  inputRef?: RefObject<HTMLTextAreaElement | null>
  className?: string
  innerClassName?: string
}

/**
 * The one input bar of this app: type or dictate, send or stop, plus whatever
 * the surrounding chat adds next to it.
 *
 * Shared by the roleplay and by every coach conversation. They had grown two
 * separate copies that drifted apart in size, focus behaviour and in what they
 * showed while recording — and an advisor switching between training and coach
 * met two different input fields.
 */
export function ChatBar({
  value,
  onChange,
  onSubmit,
  placeholder,
  disabled,
  busy,
  onStop,
  dictation,
  actions,
  above,
  below,
  size = "md",
  variant = "default",
  layout = "inline",
  autoFocus,
  rows = 1,
  inputRef,
  className,
  innerClassName,
}: Props) {
  const recording = dictation?.recording ?? false
  const transcribing = dictation?.transcribing ?? false
  const starting = dictation?.starting ?? false
  const blocked = disabled || starting || transcribing
  const submit = () => {
    if (blocked || busy) return
    if (recording && dictation) {
      dictation.stop((text) => {
        const combined = value ? `${value.trimEnd()} ${text}` : text
        onChange(combined)
        onSubmit(combined)
      })
    } else if (value.trim()) onSubmit()
  }
  const stacked = layout === "stacked"
  // Stacked shares h-9 with size="md": both must line up with the size="lg"
  // phase button (h-9) that sits level with the bar in the roleplay.
  const button =
    variant === "hero"
      ? "h-[38px] w-[38px]"
      : stacked || size === "md"
        ? "h-9 w-9"
        : "h-7 w-7"

  const field = recording ? (
    <RecordingIndicator />
  ) : (
    <Textarea
      ref={inputRef}
      data-tour="chat-eingabe"
      value={value}
      onChange={(event) => onChange(event.target.value)}
      onKeyDown={(event) => {
        if (
          event.key === "Enter" &&
          !event.shiftKey &&
          !event.nativeEvent.isComposing
        ) {
          event.preventDefault()
          submit()
        }
      }}
      placeholder={placeholder}
      rows={rows}
      autoFocus={autoFocus}
      disabled={disabled || starting || transcribing}
      aria-label={placeholder}
      aria-keyshortcuts="Enter Shift+Enter"
      className={cn(
        // The wrapper carries the frame, so the field itself stays plain.
        "max-h-40 min-h-[24px] resize-none self-center border-0 bg-transparent p-0 text-[14px] leading-normal shadow-none focus-visible:ring-0"
      )}
    />
  )

  const callActions = actions && (
    // FALLE: die Größe gehört der Leiste, nicht der Aufrufstelle. Alle
    // drei Aufrufer hatten sie fest verdrahtet, und auf der Startseite
    // (`size="md"`) blieb ein 28px-Knopf neben 36px stehen — bei
    // `items-end` sitzt sein Symbol dann 4px tiefer als die anderen.
    <fieldset
      data-tour="chat-anruf"
      disabled={disabled || recording || starting || transcribing}
      className={cn(
        "flex shrink-0 items-center gap-1",
        // Stacked rows carry labelled buttons, so only the height is pinned.
        // h-9 matches size="lg" of the phase button docked next to the bar.
        stacked
          ? "gap-2 [&_button]:h-9"
          : size === "md"
            ? "[&_button]:h-9 [&_button]:w-9"
            : "[&_button]:h-7 [&_button]:w-7"
      )}
    >
      {actions}
    </fieldset>
  )

  const microphone = dictation && (
    <div
      data-tour="chat-diktat"
      className="flex shrink-0 items-center"
      role="group"
      aria-label="Mikrofon"
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            size="icon"
            variant="ghost"
            onClick={() =>
              recording ? dictation.stop() : void dictation.start()
            }
            disabled={
              disabled || starting || transcribing || (busy && !recording)
            }
            aria-label={
              starting
                ? "Warte auf Mikrofon"
                : transcribing
                  ? "Wird transkribiert"
                  : recording
                    ? "Aufnahme beenden"
                    : "Diktat starten"
            }
            className={cn(
              button,
              "shrink-0 rounded-md text-muted-foreground hover:text-foreground",
              recording &&
                "bg-destructive/10 text-destructive hover:text-destructive"
            )}
          >
            {transcribing || starting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : recording ? (
              <Square className="h-3.5 w-3.5 fill-current" />
            ) : (
              <Mic className="h-4 w-4" />
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          {recording ? "Aufnahme beenden" : "Nachricht diktieren"}
        </TooltipContent>
      </Tooltip>
      <MicrophoneMenu
        onSelect={dictation.switchMicrophone}
        activeLabel={dictation.microphoneLabel}
        disabled={disabled || starting || transcribing || (busy && !recording)}
        className={
          stacked
            ? "h-9"
            : size === "sm"
              ? "h-7"
              : variant === "hero"
                ? "h-[38px]"
                : "h-9"
        }
      />
    </div>
  )

  const stopOrSend =
    busy && onStop ? (
      <Button
        type="button"
        size="icon"
        variant="secondary"
        onClick={onStop}
        aria-label="Antwort abbrechen"
        className={cn(
          button,
          "shrink-0 rounded-md",
          variant === "hero" && "hero-submit"
        )}
      >
        <Square className="h-3.5 w-3.5" />
      </Button>
    ) : stacked ? (
      // Labelled on purpose; disabled state carries the "nothing to send" cue,
      // so the button can stay red without looking half-active.
      <Button
        type="submit"
        disabled={(!recording && !value.trim()) || blocked || busy}
        aria-label={recording ? "Aufnahme beenden und senden" : "Senden"}
        title={recording ? "Transkribieren und direkt senden" : "Senden"}
        className="h-9 shrink-0 gap-2 rounded-md px-5 font-bold"
      >
        Senden
        <ArrowUp className="h-4 w-4" />
      </Button>
    ) : (
      <Button
        type="submit"
        size="icon"
        // Klare Zustände: leer heißt neutral, Text heißt volles Rot. Ein
        // blassrotes Rot sieht deaktiviert aus, ohne es zu sein — und
        // umgekehrt.
        variant={
          variant === "hero" || recording || value.trim()
            ? "default"
            : "secondary"
        }
        disabled={(!recording && !value.trim()) || blocked || busy}
        aria-label={recording ? "Aufnahme beenden und senden" : "Senden"}
        title={recording ? "Transkribieren und direkt senden" : "Senden"}
        className={cn(
          button,
          "shrink-0 rounded-md",
          variant === "hero" && "hero-submit"
        )}
      >
        <ArrowUp className="h-4 w-4" />
      </Button>
    )

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault()
        submit()
      }}
      className={cn("w-full", className)}
    >
      {above}
      <div
        className={cn(
          "rounded-md border border-[var(--rule)] bg-card transition-colors",
          "focus-within:border-primary/70 focus-within:ring-[3px] focus-within:ring-primary/10",
          stacked
            ? "flex flex-col gap-2 px-3 pt-2.5 pb-2"
            : cn(
                "flex items-end gap-1",
                size === "md" ? "py-2 pr-2 pl-3" : "py-1.5 pr-1.5 pl-3"
              ),
          recording && "border-destructive/40 ring-[3px] ring-destructive/15",
          variant === "hero" && "hero-composer",
          innerClassName
        )}
      >
        {stacked ? (
          <>
            {field}
            <div className="flex flex-wrap items-center gap-2">
              {callActions}
              {microphone}
              <div className="ml-auto flex shrink-0 items-center">
                {stopOrSend}
              </div>
            </div>
          </>
        ) : (
          <>
            {field}
            {callActions}
            {microphone}
            {stopOrSend}
          </>
        )}
      </div>
      {(starting || transcribing) && (
        <p role="status" className="px-1 pt-1 text-xs text-muted-foreground">
          {starting ? "Warte auf Mikrofon…" : "Wird transkribiert…"}
        </p>
      )}
      {below}
    </form>
  )
}

/** The draft stays in state while the recording indicator occupies the field. */
function RecordingIndicator() {
  return (
    <div
      role="status"
      className="flex min-w-0 flex-1 items-center gap-3 self-center overflow-hidden py-1 text-destructive"
    >
      <span className="relative flex h-2.5 w-2.5 shrink-0">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-destructive opacity-60 motion-reduce:animate-none" />
        <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-destructive" />
      </span>
      <span className="shrink-0 text-[11.5px] font-bold tracking-[0.06em] uppercase">
        Aufnahme läuft
      </span>
      <span className="flex items-end gap-[3px]" aria-hidden>
        {Array.from({ length: 22 }).map((_, index) => (
          <span
            key={index}
            className="wave-bar motion-reduce:animate-none"
            style={{
              animationDelay: `${(index % 7) * 80}ms`,
              height: `${6 + (index % 5) * 3}px`,
            }}
          />
        ))}
      </span>
    </div>
  )
}
